import { Calendar, MapPin, Users } from "lucide-react"
import { useTranslation } from "react-i18next"

import { CandidateEmailField } from "@/components/candidates/candidate-email-field"
import { EditCandidateSheet } from "@/components/candidates/edit-candidate-sheet"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Card, CardContent } from "@/components/ui/card"
import { useInitials } from "@/hooks/use-initials"
import { countryLabel } from "@/lib/countries"
import type { CandidateDetail } from "@/types/candidates"

/**
 * OKS-18 — Candidate profile card (UX Spec §Profile card).
 *
 * Read-only display. All editable surfaces (notes, tags) are owned by
 * sibling components; the action bar is rendered separately in the
 * page header.
 *
 * Locked testids:
 *   - `candidate-detail-profile` — root
 *   - `candidate-detail-bio` — bio paragraph
 *   - `candidate-detail-followers` — followers stat
 *   - `candidate-detail-location` — location stat
 *   - `candidate-detail-email` — OKS-601 contact-email row (read↔edit)
 *   - `candidate-detail-created-meta` — added-on/by line
 *
 * OKS-601 — the email row is the one interactive child (a `can_edit`
 * viewer can toggle it into an inline edit form); the card is otherwise
 * read-only display. `canEdit` gates that affordance and is threaded
 * from the page's `can_edit` prop.
 *
 * OKS-911 — the card now hosts the **Edit profile** affordance (top-right).
 * `canEditProfile` (role AND status ∈ {new, reviewed}, from the page's
 * `can_edit_profile` prop) gates it: on a decided candidate the button is
 * DOM-absent (frozen state). Distinct from `canEdit` — the notes/email/tags
 * inline editors stay editable at every status, so they must not share the
 * profile status gate. The Edit surface (`EditCandidateSheet`) owns its own
 * Sheet open state + the six-field prefilled `<Form>` PATCH.
 */
interface CandidateProfileCardProps {
  candidate: CandidateDetail
  canEdit: boolean
  /** OKS-911 — role AND status pre-decision; gates the Edit profile button. */
  canEditProfile: boolean
}

export function CandidateProfileCard({
  candidate,
  canEdit,
  canEditProfile,
}: CandidateProfileCardProps) {
  const { t, i18n } = useTranslation()
  const initials = useInitials()
  const locale = i18n.language.startsWith("fr") ? "fr" : "en"

  const locationText = formatLocation(candidate.location, t, locale)
  const followersText =
    candidate.approx_followers === null
      ? t("candidates.detail.profile.followers_unknown")
      : new Intl.NumberFormat().format(candidate.approx_followers)
  const addedDate = new Date(candidate.created_at)
  const addedRelative = relativeWhen(addedDate, t)

  return (
    <Card data-testid="candidate-detail-profile">
      <CardContent className="flex flex-col gap-4 pt-6">
        <div className="flex items-start gap-4">
          <Avatar className="size-16 shrink-0">
            <AvatarFallback className="text-base">
              {initials(candidate.display_name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h2 className="truncate font-mono text-xl">
              @{candidate.tiktok_handle}
            </h2>
            <p className="text-muted-foreground truncate text-sm">
              {candidate.display_name}
            </p>
          </div>
          {/*
            OKS-911 — Edit profile affordance, top-right of the card next to
            the identity it edits (NOT the pipeline action bar — that bar is
            for status transitions, which this feature never touches). Hidden
            on a decided candidate (frozen) via `canEditProfile`. The Sheet +
            PATCH live in `EditCandidateSheet`.
          */}
          {canEditProfile && <EditCandidateSheet candidate={candidate} />}
        </div>

        <dl className="text-muted-foreground flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <div
            data-testid="candidate-detail-followers"
            className="inline-flex items-center gap-2"
          >
            <Users
              aria-label={t("candidates.detail.profile.followers_aria")}
              className="size-4"
            />
            <dt className="sr-only">
              {t("candidates.detail.profile.followers_aria")}
            </dt>
            <dd className="tabular-nums">{followersText}</dd>
          </div>
          <div
            data-testid="candidate-detail-location"
            className="inline-flex items-center gap-2"
          >
            <MapPin
              aria-label={t("candidates.detail.profile.location_aria")}
              className="size-4"
            />
            <dt className="sr-only">
              {t("candidates.detail.profile.location_aria")}
            </dt>
            <dd className="inline-flex items-center gap-1.5">
              <span>{locationText}</span>
            </dd>
          </div>
          {/*
            OKS-601 — Contact-email row. Consolidated read↔edit surface
            (mirrors the notes editor): shows a `mailto:` link or a muted
            "No email" placeholder, and — when `canEdit` — an inline edit
            affordance. See `CandidateEmailField`.
          */}
          <CandidateEmailField
            candidateId={candidate.id}
            email={candidate.email}
            canEdit={canEdit}
          />
          <div
            data-testid="candidate-detail-created-meta"
            className="inline-flex items-center gap-2"
          >
            <Calendar className="size-4" aria-hidden="true" />
            {/*
              Phase 4 F7 — dedicated `<dt>` label key. Previous
              implementation reused the `added_meta` interpolated
              template with empty when/by placeholders, which
              produced "Added  by " (double-space, no values) for
              screen readers — malformed announcement.
            */}
            <dt className="sr-only">
              {t("candidates.detail.profile.added_meta_label")}
            </dt>
            <dd>
              <time dateTime={candidate.created_at}>
                {t("candidates.detail.added_meta", {
                  when: addedRelative,
                  by: candidate.created_by.name,
                })}
              </time>
            </dd>
          </div>
        </dl>

        <p
          data-testid="candidate-detail-bio"
          className="max-h-48 overflow-y-auto text-sm whitespace-pre-line"
        >
          {candidate.bio?.trim()
            ? candidate.bio
            : t("candidates.detail.profile.bio_empty")}
        </p>
      </CardContent>
    </Card>
  )
}

function formatLocation(
  location: CandidateDetail["location"],
  t: ReturnType<typeof useTranslation>["t"],
  locale: "en" | "fr",
): string {
  // OKS-348 — Localized country label via `lib/countries.ts#countryLabel`.
  const { city, country } = location
  const countryName = country ? countryLabel(country, locale) : null
  if (city && countryName) return `${city}, ${countryName}`
  if (countryName) return countryName
  if (city) return city
  return t("candidates.detail.profile.location_unknown")
}

function relativeWhen(
  date: Date,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  const ms = Date.now() - date.getTime()
  const days = Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)))
  if (days < 7)
    return t("candidates.list.row.days_added_relative_d", { n: days })
  if (days < 60)
    return t("candidates.list.row.days_added_relative_w", {
      n: Math.floor(days / 7),
    })
  return t("candidates.list.row.days_added_relative_mo", {
    n: Math.floor(days / 30),
  })
}
